import { integrationTesting } from '@ps/web-core-utils';
import { TrackingCategory, TrackingContext } from '@ps/web-shared-analytics';
import React from 'react';
import { useDispatch } from 'react-redux';

import { Fork, Invoke, nextPointer } from '@swf/components/StarsWizard/Flow';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import { type Dispatch } from '@swf/core/store';
import { Action } from '@swf/core/types/Wizard';
import { getDisplayName } from '@swf/core/utils';

import type { DepositPayload } from '../../types/Cashier';
import { CardType } from '../../types/cardType';
import { emailVerificationFlow } from '../../utils/emailVerificationFlow';
import BonusOffer from '../BonusOffer/BonusOffer';
import {
  EmailCode,
  EmailValidated,
  EmailVerification
} from '../EmailVerificationSteps/EmailVerificationSteps';
import Occupation from '../Occupation/Occupation';
import { animationLoadingDelay } from '../TransactionLoader/TransactionLoader';
import WizardGroup from '../WizardGroup/WizardGroup';

import DepositAmount from './components/DepositAmount/DepositAmount';
import DepositDetails from './components/DepositDetails/DepositDetails';
import DepositDetailsConfirm from './components/DepositDetailsConfirm/DepositDetailsConfirm';
import DepositMethod from './components/DepositMethod/DepositMethod';
import DepositOpenExternalApp from './components/DepositOpenExternalApp/DepositOpenExternalApp';
import DepositPendingCashout from './components/DepositPendingCashout/DepositPendingCashout';
import DepositPendingCashoutMenu from './components/DepositPendingCashoutMenu/DepositPendingCashoutMenu';
import DepositPendingCashoutResult from './components/DepositPendingCashoutResult/DepositPendingCashoutResult';
import DepositResult from './components/DepositResult/DepositResult';
import DepositResultUnknown from './components/DepositResultUnknown/DepositResultUnknown';
import DepositSummary from './components/DepositSummary/DepositSummary';
import DepositSuspended from './components/DepositSuspended/DepositSuspended';
import useDepositGuardsData from './useDepositGuardsData';
import useDepositServicesData from './useDepositServicesData';

const successDelay = integrationTesting ? 0 : animationLoadingDelay;
const depositProcessDelay = {
  name: 'depositProcessDelay',
  after: { SUCCESS_DELAY: nextPointer }
};

const jumpToFeatureStep = {
  name: 'jumpToFeatureStep',
  entry: Action.GoJump
};

const Deposit: React.FC = () => {
  const exitWizard = useWizardExit();

  const {
    modal: { enqueue }
  } = useDispatch<Dispatch>();

  const services = useDepositServicesData();
  const guards = useDepositGuardsData();

  const depositPaySystemsState = new Invoke('depositPaySystems', DepositMethod, DepositSuspended);
  const depositSavedCardsState = new Invoke('depositSavedCards', depositPaySystemsState);

  const depositBonusProcessState = new Fork(
    'shouldProcessOfferBonus',
    [new Invoke('depositProcessBonusCode', depositSavedCardsState, BonusOffer)],
    depositSavedCardsState
  );

  const depositBonusOfferState = new Fork(
    'showWelcomeBonusOffer',
    [BonusOffer, depositBonusProcessState],
    depositSavedCardsState
  );

  const crmBonusState = new Invoke('setCRMBonus', depositBonusOfferState);

  const depositCashoutMenuSelectState = new Fork(
    'depositCashoutMenuSelect',
    [
      DepositPendingCashout,
      new Invoke('cancelPendingCashouts', DepositPendingCashoutResult, DepositPendingCashout)
    ],
    [depositSavedCardsState]
  );
  const pendingCashoutFlow = new Fork(
    'showPendingCashout',
    [DepositPendingCashoutMenu],
    [crmBonusState]
  );

  const pendingCashoutsState = new Invoke('getPendingCashouts', pendingCashoutFlow);

  const depositRetryProcess = new Invoke('depositRetryProcess', DepositResult);
  const checkRetryDepositRequired = new Fork(
    'retryDepositCheck',
    [depositRetryProcess],
    [DepositResult]
  );

  const depositTransactionStatusState = new Invoke(
    'depositTransactionStatus',
    checkRetryDepositRequired,
    DepositResultUnknown
  );

  const handleResultContextState = new Fork(
    'isRedirectExternalAppContextState',
    DepositOpenExternalApp,
    depositTransactionStatusState
  );

  const depositResultContextState = new Invoke(
    'depositResultContext',
    handleResultContextState,
    DepositResultUnknown
  );

  const depositEmailCodeState = new Fork(
    'depositEmailVerificationCheck',
    [...emailVerificationFlow('depositVerifyEmailCode'), pendingCashoutsState],
    [pendingCashoutsState]
  );

  const suspendedState = new Fork('accountSuspended', [DepositSuspended], [depositEmailCodeState]);

  const forkJumpToFeature = new Invoke('setBackToRmfJumpCreator', jumpToFeatureStep);

  const handleMitIdContextState = new Fork(
    'hasDepositMitIdData',
    [forkJumpToFeature],
    [suspendedState]
  );

  const depositMitIdDataPresent = new Fork(
    'depositMitIdDataPresent',
    [new Invoke('depositResultContextMitID', handleMitIdContextState)],
    [suspendedState]
  );

  const depositResultContextPresentState = new Fork(
    'depositResultContextPresent',
    [depositResultContextState],
    [depositMitIdDataPresent]
  );

  const depositDetailsConfirmState = new Fork(
    'depositDetailsConfirmCheck',
    [DepositDetailsConfirm, DepositAmount],
    [DepositAmount]
  );

  const depositNoDetailsState = new Fork(
    'depositNoDetails',
    [depositDetailsConfirmState],
    [DepositDetails, depositDetailsConfirmState]
  );

  const forkForBreachedCumulativeDeposit = new Fork(
    'isDepositCumulative',
    [Occupation],
    [DepositSummary]
  );

  const depositOccupationState = new Fork(
    'doesAccountRequireOccupation',
    [new Invoke('depositOccupationCheck', forkForBreachedCumulativeDeposit, DepositAmount)],
    [DepositSummary]
  );

  const depositAmountBonusProcessState = new Fork(
    'shouldProcessAmountBonus',
    [new Invoke('depositProcessAmountBonusCode', depositOccupationState, DepositAmount)],
    [depositOccupationState]
  );

  const checkUserDepositFlow = new Fork(
    'checkSWFFlowGuard',
    [depositResultContextPresentState],
    [
      {
        name: 'exitDeposit',
        entry: () => {
          exitWizard();
          enqueue({ path: '/account/cashier/deposit' });
        },
        type: 'final'
      }
    ]
  );

  const setSILimits = new Invoke('fetchUserLimits', checkUserDepositFlow);

  const checkUserSessionState = new Invoke('checkSWFFlow', setSILimits);

  return (
    <TrackingContext.Provider value={{ category: TrackingCategory.Deposit }}>
      <WizardGroup
        name={getDisplayName(Deposit)}
        flow={[
          checkUserSessionState,
          setSILimits,
          checkUserDepositFlow,
          handleResultContextState,
          pendingCashoutFlow,
          DepositPendingCashoutMenu,
          depositCashoutMenuSelectState,
          crmBonusState,
          depositTransactionStatusState,
          checkRetryDepositRequired,
          depositBonusOfferState,
          depositPaySystemsState,
          DepositMethod,
          new Invoke('depositPaySystemDetails', depositNoDetailsState, DepositMethod),
          depositNoDetailsState,
          depositAmountBonusProcessState,
          forkForBreachedCumulativeDeposit,
          new Invoke('depositUpdateOccupation', DepositSummary, Occupation),
          DepositSummary,
          new Invoke('depositProcess', depositProcessDelay, DepositSummary),
          depositProcessDelay,
          DepositResult,
          DepositResultUnknown,
          DepositPendingCashoutResult,
          DepositOpenExternalApp,
          handleMitIdContextState,
          jumpToFeatureStep
        ]}
        services={services}
        guards={guards}
        delays={{
          SUCCESS_DELAY: ({
            values: {
              deposit: { type }
            }
          }: {
            values: { deposit: DepositPayload };
          }) => ([CardType.ApplePay, CardType.GooglePay].includes(type) ? 0 : successDelay)
        }}
      >
        <EmailVerification />
        <EmailCode />
        <EmailValidated />
        <DepositSuspended />
        <DepositPendingCashoutMenu />
        <DepositPendingCashout />
        <DepositPendingCashoutResult />
        <BonusOffer isDepositFlow />
        <DepositMethod />
        <DepositDetails />
        <DepositDetailsConfirm />
        <DepositAmount />
        <Occupation />
        <DepositSummary />
        <DepositResult />
        <DepositResultUnknown />
        <DepositOpenExternalApp />
      </WizardGroup>
    </TrackingContext.Provider>
  );
};

Deposit.displayName = 'Deposit';

export default Deposit;
